/**
 * Ore: what is worth mining, where it is, and how hard it is to get.
 *
 * The eight minerals arrive from Blender with a colour, a metallic and a
 * roughness — see `render/mineralMesh.ts`, which is generated. None of that
 * belongs here. This file holds the other half: which seams appear at what
 * depth, what a shard is worth, and how long a node takes to break. Keeping
 * them apart means the art can be re-exported without touching the economy,
 * and it keeps a 250 KB base64 mesh blob out of the module that the world
 * generator imports on every chunk.
 *
 * ORDER IS SAVE-STABLE. `Prop.variant` is an index into `ORE`, and a saved
 * game records nodes that have been mined out. Append; never reorder.
 */

/** The regions ore placement distinguishes. Matches `regionAt(x).name`. */
export type OreRegion = string;

export interface Ore {
  key: string;
  label: string;
  /**
   * WHAT IT IS — a native metal or a gem — declared, not inferred.
   *
   * This is the same classification the baker stamps onto the vertices as
   * `MAT.oreMetal` or `MAT.crystal`, and `check-minerals.mjs` asserts the two
   * tables still agree. It exists in both places because they answer different
   * questions: the tag decides how a facet shades, this decides which size
   * dial a cluster takes and how it is described to the player.
   *
   * It was `metallic > 0.5` for a while and that was wrong in two directions
   * at once — a re-export with different values would silently reclassify the
   * set, and a continuous number gave iron a third of the gem treatment.
   */
  kind: 'metal' | 'crystal';
  /** Salvage paid per shard. */
  value: number;
  /**
   * Shards a node yields. Bigger is not always better — a four-shard node of
   * something cheap is a longer pickup trail than a one-shard node of
   * something dear, and the trail is most of what makes mining feel paid.
   */
  shards: number;
  /**
   * Hits to break, at the laser's base damage. Native metal is tougher than
   * crystal, which is the one place this table disagrees with intuition about
   * which ore is "harder": quartz is brittle.
   */
  toughness: number;
  /**
   * Depth window in metres where this seam occurs at all, and the depth it is
   * most common at. Outside the window the weight is zero.
   */
  minDepth: number;
  maxDepth: number;
  peakDepth: number;
  /** Weight at the peak, before the region multiplier. */
  abundance: number;
  /**
   * Regions that carry more of this than the depth alone would say. A vent
   * field is where the biologicals are; the military zone is where somebody
   * already dug for titanium and left the seam open.
   */
  favours: readonly string[];
  /**
   * ONLY IN POCKETS. The seam does not occur on the open floor at any depth —
   * the only way to a shard of it is through a gate. This is what makes the
   * hidden rooms worth finding beyond their salvage purse.
   */
  hiddenOnly: boolean;
}

/**
 * The table. Depths are read off the band progression in `config.ts`: the
 * shelf is 0-240, the city 400, the military zone 640, the vents 1000-1400,
 * the abyss 1800 and the ancient structure 2400-2900.
 *
 * VALUES ARE CALIBRATED AGAINST THE UPGRADE LADDERS, not chosen by feel. A
 * level costs 110 to 2,100 and the whole tree is somewhere north of thirty
 * thousand; enemy salvage pays 10 to 25 a drop. So a node runs 66 to 360 —
 * a shelf node is three kills, the prize behind a gate is a fifth of a cheap
 * upgrade — and mining the entire open floor comes to roughly 6,400, which is
 * one ladder rather than the tree. Ore is meant to be a second income, not a
 * replacement for fighting. The census in the commit message is the check.
 */
export const ORE: readonly Ore[] = [
  { key: 'iron', label: 'IRON / NICKEL', kind: 'metal', value: 18, shards: 3, toughness: 3,
    minDepth: 0, maxDepth: 1600, peakDepth: 260, abundance: 1.0,
    favours: ['MILITARY ZONE', 'CAPITAL BERTH', 'WRECK SITE'], hiddenOnly: false },
  { key: 'copper', label: 'COPPER', kind: 'metal', value: 26, shards: 2, toughness: 3,
    minDepth: 0, maxDepth: 1400, peakDepth: 420, abundance: 0.85,
    favours: ['SUNKEN CITY'], hiddenOnly: false },
  { key: 'silica', label: 'SILICA', kind: 'crystal', value: 22, shards: 2, toughness: 2,
    minDepth: 0, maxDepth: 1100, peakDepth: 180, abundance: 0.9,
    favours: ['CORAL GARDEN', 'SEAGRASS MEADOW'], hiddenOnly: false },
  { key: 'titanium', label: 'TITANIUM', kind: 'metal', value: 46, shards: 2, toughness: 5,
    minDepth: 340, maxDepth: 2200, peakDepth: 780, abundance: 0.62,
    favours: ['MILITARY ZONE', 'CAPITAL BERTH'], hiddenOnly: false },
  { key: 'lithium', label: 'LITHIUM', kind: 'crystal', value: 52, shards: 2, toughness: 2,
    minDepth: 420, maxDepth: 2400, peakDepth: 1050, abundance: 0.80,
    favours: ['THERMAL VENTS'], hiddenOnly: false },
  { key: 'rare', label: 'RARE-EARTH', kind: 'crystal', value: 90, shards: 2, toughness: 4,
    minDepth: 900, maxDepth: 2900, peakDepth: 1750, abundance: 0.26,
    favours: ['THE ABYSS', 'ANCIENT STRUCTURE'], hiddenOnly: false },
  // MIN DEPTH 200, NOT 520, and that was a dead rule rather than a tuning
  // choice: this favours KELP FOREST, and the kelp forest is at 240 m — under
  // the old window, so the favour could never once have fired. A seam that
  // says where it likes to be and then cannot occur there is a lie in the
  // table, and it is why biological ore sat on the floor of the occurrence
  // check no matter what its abundance was raised to.
  { key: 'bio', label: 'BIOLOGICAL', kind: 'crystal', value: 70, shards: 3, toughness: 2,
    minDepth: 200, maxDepth: 2200, peakDepth: 900, abundance: 1.0,
    favours: ['THERMAL VENTS', 'KELP FOREST'], hiddenOnly: false },
  // The prize. No depth on the open floor will ever produce one.
  { key: 'abyssal', label: 'ABYSSAL CRYSTAL', kind: 'crystal', value: 180, shards: 2, toughness: 6,
    minDepth: 0, maxDepth: 1e9, peakDepth: 2400, abundance: 1.0,
    favours: [], hiddenOnly: true },
];

export const ORE_KEYS: readonly string[] = ORE.map((o) => o.key);

/** Index of an ore by key, or -1. */
export function oreIndex(key: string): number {
  return ORE.findIndex((o) => o.key === key);
}

/**
 * How likely each seam is at this depth and region, as raw weights.
 *
 * A triangular window rather than a step, so a seam thins out toward the edges
 * of its range instead of stopping at a line the player can learn. `hidden`
 * flips the whole table: inside a pocket the ordinary seams are suppressed to
 * a trickle and the hidden-only ones become the point of the room.
 */
export function oreWeights(depth: number, region: string, hidden: boolean,
                           out: number[]): number[] {
  let total = 0;
  for (let i = 0; i < ORE.length; i++) {
    const o = ORE[i]!;
    let w = 0;
    if (o.hiddenOnly) {
      // Only in a pocket, and only once the world is deep enough that finding
      // one is an event rather than the second thing that happens.
      w = hidden ? o.abundance * (0.35 + Math.min(1, depth / 1800) * 0.65) : 0;
    } else if (depth >= o.minDepth && depth <= o.maxDepth) {
      const span = depth < o.peakDepth
        ? Math.max(1, o.peakDepth - o.minDepth)
        : Math.max(1, o.maxDepth - o.peakDepth);
      w = o.abundance * Math.max(0.08, 1 - Math.abs(depth - o.peakDepth) / span);
      if (o.favours.includes(region)) w *= 2.4;
      // A pocket is not where you go for iron.
      if (hidden) w *= 0.30;
    }
    out[i] = w;
    total += w;
  }
  if (total <= 0) { out[0] = 1; total = 1; }
  for (let i = 0; i < out.length; i++) out[i] /= total;
  return out;
}

/** Pick a seam from `oreWeights` output with a 0..1 roll. */
export function pickOre(weights: readonly number[], roll: number): number {
  let acc = 0;
  for (let i = 0; i < weights.length; i++) {
    acc += weights[i]!;
    if (roll < acc) return i;
  }
  return weights.length - 1;
}

/**
 * What to call a deposit of `n` clusters. Matches the bands in `DEPOSITS`.
 *
 * Here rather than in props.ts because the sonar names deposits and props does
 * not; the generator picks a row and the only thing that survives into the
 * world is the count, so the count has to be readable back into a word.
 */
export function depositName(n: number): string {
  return n <= 1 ? 'LONE' : n <= 3 ? 'SEAM' : n <= 6 ? 'VEIN' : 'MOTHER LODE';
}

/**
 * Shards the boat can carry at once.
 *
 * BELOW THE BIGGEST DEPOSIT, deliberately. A mother lode is eleven clusters,
 * so 22 shards of abyssal crystal or 33 of iron — the cap has to be under that
 * or the richest find in the game is still one visit, which is the thing that
 * made mining feel like a trigger pull with a longer animation on it.
 *
 * At 24 a big seam is two runs and a small one is change. It is also what
 * turns a station from a shop into a destination: ore is worth nothing until
 * it is landed, so the trip back is part of the mining rather than something
 * you do afterwards.
 */
export const HOLD_CAPACITY = 24;

/**
 * Seconds per shard, from the seam's toughness.
 *
 * The first version was 0.45 + 0.18t, which put a lone crystal at under two
 * seconds and a mother lode at twenty — long enough to be a pause and not long
 * enough to be a decision. Tripled: brittle quartz is 1.6 s a shard and the
 * abyssal crystal is 3.0, so filling a hold is a minute or two of holding
 * station in the dark with the lights on and something possibly coming.
 */
export function secondsPerShard(toughness: number): number {
  return 0.9 + toughness * 0.35;
}

/**
 * How many holds a deposit is worth, roughly, per cluster's worth of shards.
 *
 * A CLUSTER'S `shards` IS ITS YIELD PER VISIT, NOT ITS WHOLE RESERVE, and
 * conflating those is why a seam still emptied in one stop. Two clusters at
 * two shards is four — under a hold of twenty-four — so a capacity that was
 * meant to force a return trip never once bound.
 *
 * At 6 the smallest seam in the game is 12 shards, half a hold; a mother lode
 * of eleven clusters is 132, which is five and a half full runs and most of
 * ten minutes of holding station. That is a mineral patch you come back to
 * rather than an object you consume, which is the whole ask.
 */
export const RESERVE_PER_SHARD = 6;
